import moment from 'moment'

export const useEvents = () => {
  const user = useUserStore()
  const { isLoading } = useGlobalState()
  const isLoadingNextEvents = ref(false)

  const fetchEvents = async (date: Date, isFetchingForNextEvents?: boolean) => {
    if (isFetchingForNextEvents) {
      isLoadingNextEvents.value = true
    } else {
      isLoading.value = true
    }

    const timeout = new Promise((_resolve, reject) =>
      setTimeout(() => reject(new Error('Request timed out after 30 seconds')), 30000)
    )

    const weekStart = moment(date).startOf('week').format('YYYY-MM-DD 00:00:00')
    const weekEnd = moment(date).endOf('week').format('YYYY-MM-DD 23:59:59')

    let calCategoryIds = ''
    if (user.calendarCategories) {
      calCategoryIds = user.calendarCategories.map((category: any) => category.id).join(',')
    } else {
      await user.fetchCalendarCategories()
      calCategoryIds = user.calendarCategories?.map((category: any) => category.id).join(',') || ''
    }

    const baseUrl = `${user?.isLoggedInAsContact ? 'css/' : ''}calendar-events`

    const params = new URLSearchParams()
    params.append('filter[inRange]', `${weekStart},${weekEnd}`)
    if (calCategoryIds?.length) {
      params.append('filter[calendar_categories_id]', calCategoryIds)
    }
    params.append('per_page', '1000')
    params.append('sort', 'start_time')
    params.append('include', 'contact,category')

    const apiUrl = `${baseUrl}?${params.toString()}`
    const fetchPromise = useApi(apiUrl, 'GET')
    const result = await Promise.race([fetchPromise, timeout])

    if (isFetchingForNextEvents) {
      isLoadingNextEvents.value = false
    } else {
      isLoading.value = false
    }

    return result?.data ?? []
  }

  const fetchAllEventTasksForDate = async (date?: Date) => {
    const targetDate = date ? moment(date) : moment()
    const formattedDate = targetDate.format('YYYY-MM-DD')

    const eventTasksResponse = await useApi(
      `calendar-event-task?filter[task.start_date]=${formattedDate}&filter[task.user]=${user.user.id}&per_page=1000&include=task,task.userRelation,task.fromContactRelation,task.status,task.taskPriority,task.taskChecklist,task.taskChecklist.checkListItem,task.taskChecklist.checkList`,
      'GET'
    )

    const allEventTasks = eventTasksResponse?.data ?? []

    const tasksByEventId: Record<number, any[]> = {}

    allEventTasks.forEach((eventTask: any) => {
      if (!tasksByEventId[eventTask.calendar_event_id]) {
        tasksByEventId[eventTask.calendar_event_id] = []
      }
      if (eventTask.task) {
        tasksByEventId[eventTask.calendar_event_id].push(eventTask.task)
      }
    })

    return tasksByEventId
  }

  const fetchAllEventTasksForWeek = async (weekEvents: any[]) => {
    if (!weekEvents || weekEvents.length === 0) {
      return {}
    }

    const eventIds = weekEvents.map((event) => event.id).filter(Boolean)

    if (eventIds.length === 0) {
      return {}
    }

    const eventTasksResponse = await useApi(
      `calendar-event-task?filter[calendar_event_id]=${eventIds.join(
        ','
      )}&per_page=1000&include=task,task.userRelation,task.fromContactRelation,task.status,task.taskPriority,task.taskChecklist,task.taskChecklist.checkListItem,task.taskChecklist.checkList`,
      'GET'
    )

    const allEventTasks = eventTasksResponse?.data ?? []

    const tasksByEventId: Record<number, any[]> = {}

    allEventTasks.forEach((eventTask: any) => {
      if (!tasksByEventId[eventTask.calendar_event_id]) {
        tasksByEventId[eventTask.calendar_event_id] = []
      }
      if (eventTask.task) {
        tasksByEventId[eventTask.calendar_event_id].push(eventTask.task)
      }
    })

    return tasksByEventId
  }

  const loadNextTwoEvents = async (selectedDate: Date) => {
    isLoadingNextEvents.value = true

    const timeout = new Promise((_resolve, reject) =>
      setTimeout(() => reject(new Error('Request timed out after 30 seconds')), 30000)
    )

    const startDate = moment(selectedDate).add(1, 'days').format('YYYY-MM-DD 00:00:00')
    const endDate = moment(selectedDate).add(8, 'days').format('YYYY-MM-DD 23:59:59')

    let calCategoryIds = ''
    if (user.calendarCategories) {
      calCategoryIds = user.calendarCategories.map((category: any) => category.id).join(',')
    } else {
      await user.fetchCalendarCategories()
      calCategoryIds = user.calendarCategories?.map((category: any) => category.id).join(',') || ''
    }

    const baseUrl = `${user?.isLoggedInAsContact ? 'css/' : ''}calendar-events`

    const params = new URLSearchParams()
    params.append('filter[inRange]', `${startDate},${endDate}`)
    if (calCategoryIds?.length) {
      params.append('filter[calendar_categories_id]', calCategoryIds)
    }
    params.append('per_page', '2')
    params.append('sort', 'start_time')
    params.append('include', 'contact,category')

    const apiUrl = `${baseUrl}?${params.toString()}`

    try {
      const fetchPromise = useApi(apiUrl, 'GET')
      const result = await Promise.race([fetchPromise, timeout])
      isLoadingNextEvents.value = false
      return result?.data ?? []
    } catch (error) {
      isLoadingNextEvents.value = false
      throw error
    }
  }

  return {
    fetchEvents,
    fetchAllEventTasksForDate,
    fetchAllEventTasksForWeek,
    loadNextTwoEvents,
    isLoadingNextEvents
  }
}
